/**
 * WMS Dashboard Widget Registry
 *
 * Defines all available widgets and their configurations.
 *
 * @author BizClick WMS
 * @version 1.0.0
 */

import { WidgetDefinition, WidgetCategory } from './types';

/**
 * All available widgets in the system
 */
export const WIDGET_REGISTRY: WidgetDefinition[] = [
  // === ORDERS ===
  {
    id: 'order-summary',
    name: 'Ordreoversikt',
    description: 'Sammendrag av ordrestatus med trender',
    category: 'orders',
    defaultSize: 'medium',
    allowedSizes: ['small', 'medium', 'large'],
    defaultRefreshInterval: 30,
    icon: 'ClipboardList',
    minWidth: 2,
    minHeight: 2,
  },
  {
    id: 'orders-pending',
    name: 'Ventende ordrer',
    description: 'Antall ordrer som venter på behandling',
    category: 'orders',
    defaultSize: 'small',
    allowedSizes: ['small', 'medium'],
    defaultRefreshInterval: 30,
    icon: 'Clock',
    minWidth: 2,
    minHeight: 2,
  },

  // === PICKING ===
  {
    id: 'picking-stats',
    name: 'Plukkstatistikk',
    description: 'Oversikt over plukkaktivitet og ytelse',
    category: 'picking',
    defaultSize: 'large',
    allowedSizes: ['medium', 'large', 'wide'],
    defaultRefreshInterval: 30,
    icon: 'PackageSearch',
    minWidth: 3,
    minHeight: 2,
  },
  {
    id: 'picking-progress',
    name: 'Plukkfremgang',
    description: 'Daglig plukkfremgang mot mål',
    category: 'picking',
    defaultSize: 'medium',
    allowedSizes: ['small', 'medium', 'large'],
    defaultRefreshInterval: 60,
    icon: 'Target',
    minWidth: 2,
    minHeight: 2,
  },
  {
    id: 'active-pickers',
    name: 'Aktive plukkere',
    description: 'Antall plukkere i aktivitet',
    category: 'picking',
    defaultSize: 'small',
    allowedSizes: ['small', 'medium'],
    defaultRefreshInterval: 30,
    icon: 'Users',
    minWidth: 2,
    minHeight: 2,
  },

  // === RECEIVING ===
  {
    id: 'receiving-stats',
    name: 'Mottaksstatistikk',
    description: 'Oversikt over dagens mottak og forventede leveranser',
    category: 'receiving',
    defaultSize: 'medium',
    allowedSizes: ['small', 'medium', 'large'],
    defaultRefreshInterval: 60,
    icon: 'Truck',
    minWidth: 2,
    minHeight: 2,
  },
  {
    id: 'pending-putaway',
    name: 'Venter plassering',
    description: 'Varer mottatt som venter på plassering',
    category: 'receiving',
    defaultSize: 'small',
    allowedSizes: ['small', 'medium'],
    defaultRefreshInterval: 60,
    icon: 'ArrowDownToLine',
    minWidth: 2,
    minHeight: 2,
  },

  // === INVENTORY ===
  {
    id: 'inventory-alerts',
    name: 'Lagervarsler',
    description: 'Kritiske varsler om lagerbeholdning',
    category: 'inventory',
    defaultSize: 'tall',
    allowedSizes: ['medium', 'large', 'tall'],
    defaultRefreshInterval: 60,
    icon: 'AlertTriangle',
    minWidth: 3,
    minHeight: 3,
  },
  {
    id: 'warehouse-capacity',
    name: 'Kapasitet',
    description: 'Lagerfyllingsgrad per sone',
    category: 'inventory',
    defaultSize: 'large',
    allowedSizes: ['medium', 'large', 'wide'],
    defaultRefreshInterval: 300,
    icon: 'Warehouse',
    minWidth: 3,
    minHeight: 2,
  },

  // === SHIPPING ===
  {
    id: 'shipping-stats',
    name: 'Utsendingsstatistikk',
    description: 'Oversikt over utsendinger og klar-status',
    category: 'shipping',
    defaultSize: 'medium',
    allowedSizes: ['small', 'medium', 'large'],
    defaultRefreshInterval: 30,
    icon: 'Send',
    minWidth: 2,
    minHeight: 2,
  },
  {
    id: 'ready-to-ship',
    name: 'Klar til sending',
    description: 'Ordrer klare for utsending',
    category: 'shipping',
    defaultSize: 'small',
    allowedSizes: ['small', 'medium'],
    defaultRefreshInterval: 30,
    icon: 'PackageCheck',
    minWidth: 2,
    minHeight: 2,
  },

  // === KPI ===
  {
    id: 'kpi-grid',
    name: 'KPI-oversikt',
    description: 'Nøkkeltall for lagerytelse',
    category: 'kpi',
    defaultSize: 'wide',
    allowedSizes: ['large', 'wide'],
    defaultRefreshInterval: 60,
    icon: 'TrendingUp',
    minWidth: 4,
    minHeight: 2,
  },
  {
    id: 'pick-efficiency',
    name: 'Plukkeffektivitet',
    description: 'Plukkeffektivitet som prosent',
    category: 'kpi',
    defaultSize: 'small',
    allowedSizes: ['small', 'medium'],
    defaultRefreshInterval: 60,
    icon: 'Gauge',
    minWidth: 2,
    minHeight: 2,
  },
  {
    id: 'on-time-delivery',
    name: 'Leveringspresisjon',
    description: 'Andel ordrer levert i tide',
    category: 'kpi',
    defaultSize: 'small',
    allowedSizes: ['small', 'medium'],
    defaultRefreshInterval: 60,
    icon: 'Timer',
    minWidth: 2,
    minHeight: 2,
  },

  // === ACTIVITY ===
  {
    id: 'activity-feed',
    name: 'Aktivitetslogg',
    description: 'Sanntids aktivitetsstrøm fra lageret',
    category: 'activity',
    defaultSize: 'tall',
    allowedSizes: ['medium', 'large', 'tall'],
    defaultRefreshInterval: 10,
    icon: 'Activity',
    minWidth: 3,
    minHeight: 3,
  },
  {
    id: 'hourly-activity-chart',
    name: 'Timebasert aktivitet',
    description: 'Graf over aktivitet per time',
    category: 'activity',
    defaultSize: 'wide',
    allowedSizes: ['large', 'wide'],
    defaultRefreshInterval: 60,
    icon: 'BarChart3',
    minWidth: 4,
    minHeight: 2,
  },

  // === OPERATORS ===
  {
    id: 'operator-status',
    name: 'Operatørstatus',
    description: 'Oversikt over operatører og deres oppgaver',
    category: 'operators',
    defaultSize: 'large',
    allowedSizes: ['medium', 'large', 'tall'],
    defaultRefreshInterval: 30,
    icon: 'Users',
    minWidth: 3,
    minHeight: 3,
  },
  {
    id: 'top-performers',
    name: 'Topp-ytere',
    description: 'Operatører med best ytelse i dag',
    category: 'operators',
    defaultSize: 'medium',
    allowedSizes: ['small', 'medium', 'large'],
    defaultRefreshInterval: 60,
    icon: 'Trophy',
    minWidth: 2,
    minHeight: 2,
  },

  // === SYSTEM ===
  {
    id: 'clock',
    name: 'Klokke',
    description: 'Dato og klokkeslett',
    category: 'system',
    defaultSize: 'small',
    allowedSizes: ['small', 'medium'],
    defaultRefreshInterval: 0, // No API refresh, updates every second
    icon: 'Clock',
    minWidth: 2,
    minHeight: 2,
  },
  {
    id: 'weather',
    name: 'Vær',
    description: 'Lokalt værvarsel (for logistikkplanlegging)',
    category: 'system',
    defaultSize: 'small',
    allowedSizes: ['small', 'medium'],
    defaultRefreshInterval: 300,
    icon: 'CloudSun',
    minWidth: 2,
    minHeight: 2,
  },
];

/**
 * Get widget definition by ID
 */
export const getWidgetById = (id: string): WidgetDefinition | undefined => {
  return WIDGET_REGISTRY.find((w) => w.id === id);
};

/**
 * Get widgets by category
 */
export const getWidgetsByCategory = (category: WidgetCategory): WidgetDefinition[] => {
  return WIDGET_REGISTRY.filter((w) => w.category === category);
};

/**
 * Get all categories with their widgets
 */
export const getWidgetsByCategories = (): Map<WidgetCategory, WidgetDefinition[]> => {
  const map = new Map<WidgetCategory, WidgetDefinition[]>();

  WIDGET_REGISTRY.forEach((widget) => {
    const existing = map.get(widget.category) || [];
    existing.push(widget);
    map.set(widget.category, existing);
  });

  return map;
};

/**
 * Default layout for new dashboards
 */
export const getDefaultLayout = () => ({
  id: 'default',
  name: 'Standard Dashboard',
  widgets: [
    // Top row - Key metrics
    {
      instanceId: 'inst-1',
      widgetId: 'clock',
      position: { x: 0, y: 0 },
      size: { width: 2, height: 2 },
      settings: { refreshInterval: 0, showHeader: true },
    },
    {
      instanceId: 'inst-2',
      widgetId: 'order-summary',
      position: { x: 2, y: 0 },
      size: { width: 3, height: 2 },
      settings: { refreshInterval: 30, showHeader: true },
    },
    {
      instanceId: 'inst-3',
      widgetId: 'picking-stats',
      position: { x: 5, y: 0 },
      size: { width: 4, height: 2 },
      settings: { refreshInterval: 30, showHeader: true },
    },
    {
      instanceId: 'inst-4',
      widgetId: 'shipping-stats',
      position: { x: 9, y: 0 },
      size: { width: 3, height: 2 },
      settings: { refreshInterval: 30, showHeader: true },
    },
    // Middle row
    {
      instanceId: 'inst-5',
      widgetId: 'kpi-grid',
      position: { x: 0, y: 2 },
      size: { width: 6, height: 2 },
      settings: { refreshInterval: 60, showHeader: true },
    },
    {
      instanceId: 'inst-6',
      widgetId: 'hourly-activity-chart',
      position: { x: 6, y: 2 },
      size: { width: 6, height: 2 },
      settings: { refreshInterval: 60, showHeader: true },
    },
    // Bottom row
    {
      instanceId: 'inst-7',
      widgetId: 'operator-status',
      position: { x: 0, y: 4 },
      size: { width: 4, height: 4 },
      settings: { refreshInterval: 30, showHeader: true },
    },
    {
      instanceId: 'inst-8',
      widgetId: 'activity-feed',
      position: { x: 4, y: 4 },
      size: { width: 4, height: 4 },
      settings: { refreshInterval: 10, showHeader: true },
    },
    {
      instanceId: 'inst-9',
      widgetId: 'inventory-alerts',
      position: { x: 8, y: 4 },
      size: { width: 4, height: 4 },
      settings: { refreshInterval: 60, showHeader: true },
    },
  ],
  gridColumns: 12,
  gridRows: 8,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  isDefault: true,
});
